/* Ask Nova (Dori Product Pal) on the PDP: instant panel + intent-based warm-up (draft theme only).
   Before: nothing was fetched until the card was near; on Ask the panel showed Dori's grey
   skeleton for ~6s (a 1 MB widget bundle downloaded, parsed, then asked Dori for its data).
   Now (DevOps DO-04: pinned version, no bare-idle download for every visitor):
   - warm + start: when the Ask card has been on screen for 2s (engaged reader), or on hover / touch /
             focus of Ask: preconnect to unpkg + Dori's API, preload the pinned loader + main bundle
             and inject the loader, so Dori is usually mounted (hidden) before the click. Never on bare
             idle; with Save-Data / 2G only on intent;
   - click:  the panel opens at once on server-rendered questions + an ask box. A question asked
             before Dori is ready is shown with a typing indicator and handed to Dori when it mounts.
   To update Dori: bump DORI_VER and DORI_MAIN (the loader imports ./assets/main-<hash>.js; see
   https://unpkg.com/@dori-ai/dori-pdp-widget@<ver>/dist/widget.js). */
(() => {
  const d = document;
  const pal = d.querySelector('[data-nhp-pal]');
  if (!pal || pal.dataset.nhnInit) return;
  pal.dataset.nhnInit = '1';

  const DORI_VER = '0.6.66';
  const DORI_BASE = `https://unpkg.com/@dori-ai/dori-pdp-widget@${DORI_VER}/dist/`;
  const LOADER = `${DORI_BASE}widget.js`;
  const DORI_MAIN = `${DORI_BASE}assets/main-BCcEPivt.js`;
  const btn = pal.querySelector('[data-nhp-pal-toggle]');
  const panel = pal.querySelector('.nhp-pal__panel');
  const slot = d.getElementById('dori-widget-container');
  const ui = pal.querySelector('[data-nhn]');
  const log = pal.querySelector('[data-nhn-log]');
  const form = pal.querySelector('[data-nhn-form]');
  const input = form && form.querySelector('input');
  const send = form && form.querySelector('button[type="submit"]');
  const conn = navigator.connection || {};
  const frugal = conn.saveData || /(^|-)2g$/.test(conn.effectiveType || '');

  let warmed = false;
  let started = false;
  let ready = false;
  let pending = '';

  const addLink = (rel, href, cors) => {
    if (d.querySelector(`link[rel="${rel}"][href="${href}"]`)) return;
    const l = d.createElement('link');
    l.rel = rel; l.href = href;
    if (cors) l.crossOrigin = '';
    d.head.appendChild(l);
  };

  // 1. Warm: preconnect + preload the pinned loader and main bundle (download/compile only).
  const warm = (intentful) => {
    if (warmed || (frugal && !intentful)) return;
    warmed = true;
    addLink('preconnect', 'https://unpkg.com', true);
    addLink('preconnect', 'https://standardapi.dori.tech', true);
    addLink('modulepreload', LOADER, true);
    addLink('modulepreload', DORI_MAIN, true);
  };

  // 2. Start: inject Dori's loader (it fetches the bot data and mounts on the next interaction).
  const start = () => {
    if (started) return;
    started = true;
    const inject = () => {
      const s = d.createElement('script');
      s.type = 'module';
      s.async = true;
      s.id = 'dori-react-widget';
      s.dataset.sharingId = pal.dataset.sharingId;
      s.dataset.productId = pal.dataset.productId;
      if (pal.dataset.currency) s.dataset.currency = pal.dataset.currency;
      s.src = LOADER;
      // Dori's loader waits for a user interaction before mounting; nudge it once it is ready.
      s.addEventListener('load', () => window.dispatchEvent(new Event('pointerdown')));
      d.body.appendChild(s);
      watch();
    };
    inject();
  };

  // Dori renders into an open shadow root inside #mori-react-widget-host.
  const doriRoot = () => {
    const host = d.getElementById('mori-react-widget-host');
    const inner = host && host.firstElementChild;
    return (inner && inner.shadowRoot) || null;
  };
  const doriInput = () => { const r = doriRoot(); return r && r.querySelector('textarea, input[type="text"], input:not([type])'); };

  // Key used to find the shopper's question echoed in Dori's chat (its cards carry a label prefix and an arrow).
  const keyOf = (q) => (q || '').replace(/→/g, '').trim().slice(-24);
  const echoed = (q) => { const k = keyOf(q); return k.length > 3 && answerText().includes(k); };
  const answered = (q) => {
    const t = answerText().replace(/AI can make mistakes\.?\s*Check important info\.?/gi, '');
    const k = keyOf(q);
    const i = k ? t.lastIndexOf(k) : -1;
    return i >= 0 && t.slice(i + k.length).trim().length > 8;
  };
  const submitIn = (field) => {
    const r = doriRoot();
    const sendBtn = r && [...r.querySelectorAll('button')].find(bt => /send/i.test(bt.getAttribute('aria-label') || ''));
    if (sendBtn && !sendBtn.disabled) sendBtn.click();
    else field.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', keyCode: 13, which: 13, bubbles: true }));
  };
  const typeIn = (field, q) => {
    const proto = field.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    Object.getOwnPropertyDescriptor(proto, 'value').set.call(field, q);
    field.dispatchEvent(new Event('input', { bubbles: true }));
  };
  // Type + send once, then confirm: resend only if the text is still sitting unsent in Dori's box
  // (never twice if Dori took it), and swap to Dori's view only once it shows the question.
  const sendToDori = (q) => {
    let tries = 0;
    let typed = false;
    const step = () => {
      const field = doriInput();
      if (echoed(q)) { reveal(); watchAnswer(q); return; }
      if (field && !field.disabled && !field.readOnly) {
        if (!typed || (field.value === '' && tries === 4)) { typeIn(field, q); typed = true; setTimeout(() => submitIn(field), 80); }
        else if (field.value === q) submitIn(field);
      }
      if (++tries > 9) { reveal(); watchAnswer(q); return; } // worst case the question stays typed in Dori's box
      setTimeout(step, 500);
    };
    step();
  };
  const reveal = () => {
    const hadFocus = ui.contains(d.activeElement);
    pal.classList.add('is-dori');
    // Our panel is now hidden: keep keyboard/screen-reader users in the conversation.
    const f = doriInput();
    if (f && hadFocus) f.focus({ preventScroll: true });
  };
  const handOver = () => {
    if (pending) { const q = pending; pending = ''; sendToDori(q); }
    else reveal();
  };

  let watching = false;
  const watch = () => {
    if (watching) return;
    watching = true;
    const t0 = performance.now();
    const tick = () => {
      if (doriInput()) {
        ready = true;
        if (pal.classList.contains('is-open')) handOver();
        return;
      }
      if (performance.now() - t0 > 20000) { slow(); return; }
      setTimeout(tick, 100);
    };
    tick();
  };

  // Friendly fallback (CRO-03): never leave a shopper on a raw error or a silent wait.
  const FAQ = '/pages/sex-toys-faq';
  const CONTACT = '/pages/contact-us';
  let note = null;
  const showNote = (kind) => {
    if (!note) {
      note = d.createElement('p');
      note.className = 'nhn__note';
      note.setAttribute('role', 'status');
      panel.appendChild(note);
    }
    note.innerHTML = kind === 'error'
      ? `Nova couldn't answer that just now. Try asking again, or find answers in our <a href="${FAQ}">FAQ</a> or <a href="${CONTACT}">contact us</a>.`
      : `Nova is taking a moment. Keep waiting, or find quick answers in our <a href="${FAQ}">FAQ</a> or <a href="${CONTACT}">contact us</a>.`;
    note.hidden = false;
    // Fix batch 2026-09-29 (FE-F04): the note is added under Dori's panel, where the dock and the sticky
    // add-to-bag cover it on phones. If the shopper is still at the panel, bring the note just into the
    // clear band above the bottom bars (scroll-margin in nh-fixbatch.css); never pull them back from elsewhere.
    requestAnimationFrame(() => {
      const r = panel.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      note.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
    });
  };
  const hideNote = () => { if (note) note.hidden = true; };
  const ERR = /an error has occurred|something went wrong|error occurred|failed to (fetch|load)/i;
  const answerText = () => {
    const r = doriRoot();
    if (!r) return '';
    let t = '';
    const w = d.createTreeWalker(r, NodeFilter.SHOW_TEXT);
    for (let n = w.nextNode(); n; n = w.nextNode()) { const tag = n.parentNode && n.parentNode.nodeName; if (tag !== 'STYLE' && tag !== 'SCRIPT') t += n.nodeValue; }
    // Dori's transient status lines are not an answer.
    return t.replace(/(typing|processing|thinking|searching)(\.\.\.|…)?/gi, '');
  };
  // Hide Dori's raw error line and show ours instead.
  const scrubErrors = () => {
    const r = doriRoot();
    if (!r) return false;
    let found = false;
    r.querySelectorAll('*').forEach(el => {
      if (el.children.length || el.tagName === 'STYLE' || !ERR.test(el.textContent || '')) return;
      found = true;
      el.style.display = 'none';
    });
    if (found) showNote('error');
    return found;
  };
  let answerTimer = 0;
  let answerMo = null;
  const watchAnswer = (q) => {
    const r = doriRoot();
    if (!r) return;
    const base = answerText().length;
    const done = () => (q ? answered(q) : Math.abs(answerText().length - base) > 40);
    hideNote();
    clearTimeout(answerTimer);
    if (answerMo) answerMo.disconnect();
    answerMo = new MutationObserver(() => {
      if (scrubErrors()) return;
      if (done()) { hideNote(); clearTimeout(answerTimer); }
    });
    answerMo.observe(r, { childList: true, subtree: true, characterData: true });
    answerTimer = setTimeout(() => { if (!scrubErrors() && !done()) showNote('slow'); }, 8000);
  };
  // A question sent inside Dori's own UI (its suggestion cards, its Send button or Enter).
  if (slot) {
    slot.addEventListener('click', (e) => {
      const hit = e.composedPath().find(n => n.tagName === 'BUTTON');
      if (hit && ready && pal.classList.contains('is-dori')) { const t = (hit.textContent || '').trim(); setTimeout(() => watchAnswer(t.length > 8 ? t : ''), 0); }
    });
    slot.addEventListener('keydown', (e) => {
      const f = e.composedPath()[0];
      if (e.key === 'Enter' && !e.shiftKey && ready && e.isTrusted && f && 'value' in f) { const q = f.value; setTimeout(() => watchAnswer(q), 0); }
    });
  }

  const slow = () => {
    const t = log && log.querySelector('.nhn__typing');
    if (t) t.remove();
    showNote('error');
    if (send) send.disabled = false;
    if (input) input.disabled = false;
  };

  const ask = (q) => {
    q = (q || '').trim();
    if (!q) return;
    if (ready) { pending = q; handOver(); return; }
    pending = q;
    ui.classList.add('is-asking');
    log.textContent = '';
    const me = d.createElement('p');
    me.className = 'nhn__me';
    me.textContent = q;
    const typing = d.createElement('p');
    typing.className = 'nhn__typing';
    typing.innerHTML = '<span class="nhn__dots" aria-hidden="true"><i></i><i></i><i></i></span><span>Nova is thinking…</span>';
    log.append(me, typing);
    if (input) { input.value = ''; input.disabled = true; }
    if (send) send.disabled = true;
    clearTimeout(answerTimer);
    answerTimer = setTimeout(() => { if (!ready) showNote('slow'); }, 8000);
    warm(true); start();
  };

  // Triggers: card on screen for 2s (after load), or intent on Ask.
  const armWarm = () => {
    if (!('IntersectionObserver' in window)) return;
    let timer = 0;
    const io = new IntersectionObserver(([e]) => {
      clearTimeout(timer);
      if (e.isIntersecting) timer = setTimeout(() => { io.disconnect(); warm(false); if (!frugal) start(); }, 2000);
    }, { threshold: 0.5 });
    io.observe(btn);
  };
  if (d.readyState === 'complete') armWarm(); else window.addEventListener('load', armWarm, { once: true });

  const intent = () => { warm(true); start(); };
  btn.addEventListener('pointerenter', intent, { once: true, passive: true });
  btn.addEventListener('touchstart', intent, { once: true, passive: true });
  btn.addEventListener('focus', intent, { once: true });

  btn.addEventListener('click', () => {
    const open = btn.getAttribute('aria-expanded') !== 'true';
    btn.setAttribute('aria-expanded', String(open));
    pal.classList.toggle('is-open', open);
    panel.hidden = !open;
    if (open) {
      intent();
      if (ready) handOver();
    }
  });

  pal.querySelectorAll('[data-nhn-q]').forEach(b => b.addEventListener('click', () => ask(b.textContent)));
  if (form) form.addEventListener('submit', (e) => { e.preventDefault(); ask(input.value); });
})();
